<script setup>
import { computed, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { ArrowRight01Icon } from '@hugeicons/core-free-icons';

import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';
import LifecycleQuickActionsMenu from './LifecycleQuickActionsMenu.vue';

const props = defineProps({
  stages: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'sm',
    validator: value => ['sm', 'md'].includes(value),
  },
});

const emit = defineEmits(['select', 'next']);

const selectedStageId = defineModel({
  type: [Number, String, null],
  default: null,
});

const isMenuOpen = ref(false);

const sortStages = stages => {
  return [...stages].sort((a, b) => {
    if (a.category === b.category) return a.position - b.position;
    return a.category === 'lifecycle_stage' ? -1 : 1;
  });
};

const orderedStages = computed(() => sortStages(props.stages));

const selectedStage = computed(
  () =>
    orderedStages.value.find(
      stage => Number(stage.id) === Number(selectedStageId.value)
    ) || null
);

const nextStageId = computed(() => {
  const stageIds = orderedStages.value.map(stage => Number(stage.id));
  if (!stageIds.length) return null;

  if (selectedStageId.value === null || selectedStageId.value === '') {
    return stageIds[0] ?? null;
  }

  const currentIndex = stageIds.findIndex(
    id => id === Number(selectedStageId.value)
  );

  if (currentIndex === -1) return stageIds[0] ?? null;

  return stageIds[currentIndex + 1] ?? null;
});

const nextStage = computed(
  () =>
    orderedStages.value.find(
      stage => Number(stage.id) === Number(nextStageId.value)
    ) || null
);

const nextStageTooltip = computed(() => {
  if (!nextStage.value) return null;
  const name = nextStage.value.name?.trim();
  if (!name) return nextStage.value.icon || null;
  return nextStage.value.icon ? `${nextStage.value.icon} ${name}` : name;
});

const sizeClasses = computed(() =>
  props.size === 'md'
    ? { main: 'h-8 w-fit pl-3 pr-3.5 text-sm', action: 'h-8 w-9', icon: 24 }
    : { main: 'h-7 w-fit pl-2 pr-3 text-sm', action: 'h-7 w-9', icon: 22 }
);

const buttonClasses =
  'border-0 bg-transparent transition-colors hover:enabled:bg-n-slate-9/10 focus-visible:bg-n-slate-9/10 focus-visible:outline-none';

const toggleMenu = () => {
  if (props.disabled) return;
  isMenuOpen.value = !isMenuOpen.value;
};

const closeMenu = () => {
  isMenuOpen.value = false;
};

const selectStage = stageId => {
  selectedStageId.value = stageId;
  emit('select', stageId);
  closeMenu();
};

const goToNextStage = () => {
  if (!nextStageId.value || props.disabled) return;
  selectedStageId.value = nextStageId.value;
  emit('next', nextStageId.value);
  emit('select', nextStageId.value);
};
</script>

<template>
  <div
    v-on-click-outside="closeMenu"
    class="relative inline-flex"
    :class="{ 'pointer-events-none opacity-50': disabled }"
  >
    <div
      class="inline-flex items-stretch overflow-hidden rounded-lg outline outline-1 outline-n-strong"
    >
      <button
        type="button"
        class="inline-flex items-center gap-1.5 rounded-l-lg rounded-r-none text-n-slate-12"
        :class="[buttonClasses, sizeClasses.main]"
        :aria-expanded="isMenuOpen"
        aria-haspopup="listbox"
        @click="toggleMenu"
      >
        <span
          v-if="selectedStage?.icon"
          class="inline-flex shrink-0 items-center justify-center text-[15px] leading-none"
        >
          {{ selectedStage.icon }}
        </span>
        <span class="whitespace-nowrap font-medium">
          {{
            selectedStage?.name || $t('LIFECYCLE_STAGES.CONTACT_FIELD.NO_STAGE')
          }}
        </span>
      </button>

      <span class="w-px shrink-0 self-stretch bg-n-strong" aria-hidden="true" />

      <button
        v-tooltip.top="nextStageTooltip"
        type="button"
        class="inline-flex items-center justify-center rounded-l-none rounded-r-lg text-n-slate-11 disabled:cursor-not-allowed disabled:opacity-50"
        :class="[buttonClasses, sizeClasses.action]"
        :disabled="!nextStageId"
        :aria-label="$t('LIFECYCLE_STAGES.CONTACT_FIELD.NEXT_STAGE')"
        @click="goToNextStage"
      >
        <HugeIcon
          :icon="ArrowRight01Icon"
          :size="sizeClasses.icon"
          class="shrink-0"
        />
      </button>
    </div>

    <transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="translate-y-1 scale-95 opacity-0"
      enter-to-class="translate-y-0 scale-100 opacity-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="translate-y-0 scale-100 opacity-100"
      leave-to-class="translate-y-1 scale-95 opacity-0"
    >
      <div
        v-if="isMenuOpen"
        class="absolute left-0 top-full z-20 mt-1 w-fit overflow-hidden rounded-xl border border-n-weak bg-n-surface-1 p-0 shadow-xl"
      >
        <LifecycleQuickActionsMenu
          :stages="stages"
          :selected-stage-id="selectedStageId"
          fit-content
          @select="selectStage"
        />
      </div>
    </transition>
  </div>
</template>
